export const IAC_RUNNER_ANNOTATION = 'techne.com/iac-runner';
export const IAC_WORKDIR_ANNOTATION = 'techne.com/iac-workdir';
export const IAC_REPO_ANNOTATION = 'techne.com/iac-repo';

export type IacRunAction = 'plan' | 'apply' | 'destroy' | 'remove-comp';
export type IacRunStatus =
  | 'Queued'
  | 'Running'
  | 'Succeeded'
  | 'Failed'
  | 'Cancelled';

export const iacActionLabel = (action: IacRunAction): string =>
  action === 'remove-comp' ? 'Remover Compartment' : action;

export const iacStatusLabel = (status: IacRunStatus): string =>
  ((
    {
      Queued: 'Aguardando',
      Running: 'Executando',
      Succeeded: 'Concluído',
      Failed: 'Falhou',
      Cancelled: 'Cancelado',
    } as const
  )[status]);

export type IacRun = {
  id: string;
  entityRef: string;
  action: IacRunAction;
  status: IacRunStatus;
  jobName: string;
  organization: string;
  project: string;
  repository: string;
  workdir: string;
  branch: string;
  triggeredBy: string;
  errorMessage: string | null;
  logTail: string | null;
  orphanCompartmentId: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CompartmentRemovalSchedule = {
  id: string;
  entityRef: string;
  compartmentId: string;
  status: 'Scheduled';
  requestedBy: string;
  scheduledAt: string;
  estimatedEligibleAt: string;
  nextAttemptAt: string;
  lastAttemptAt: string | null;
  lastError: string | null;
};

export function removalDaysRemaining(
  schedule: CompartmentRemovalSchedule,
  now = new Date(),
): number {
  return Math.max(
    0,
    Math.ceil(
      (new Date(schedule.estimatedEligibleAt).getTime() - now.getTime()) /
        (24 * 60 * 60 * 1000),
    ),
  );
}

export type OrphanCompartmentState = {
  compartmentId: string;
  destroyedAt: string;
};

export function getLatestSuccessfulLifecycleRun(
  runs: IacRun[],
): IacRun | undefined {
  return runs.find(
    run =>
      run.status === 'Succeeded' &&
      (run.action === 'apply' ||
        run.action === 'destroy' ||
        run.action === 'remove-comp'),
  );
}

/** Destroy só faz sentido quando a infra ainda está provisionada (último apply). */
export function canRunDestroy(runs: IacRun[]): boolean {
  const latest = getLatestSuccessfulLifecycleRun(runs);
  return !latest || latest.action === 'apply';
}

export function getOrphanCompartmentState(
  runs: IacRun[],
): OrphanCompartmentState | undefined {
  const latestLifecycle = getLatestSuccessfulLifecycleRun(runs);
  if (
    latestLifecycle?.action !== 'destroy' ||
    !latestLifecycle.orphanCompartmentId
  ) {
    return undefined;
  }
  return {
    compartmentId: latestLifecycle.orphanCompartmentId,
    destroyedAt: latestLifecycle.finishedAt ?? latestLifecycle.updatedAt,
  };
}

export type IacRepoTarget = {
  organization: string;
  project: string;
  repository: string;
  workdir: string;
};

export function parseIacRepoAnnotation(
  repo: string | undefined,
  workdir: string | undefined,
): IacRepoTarget | undefined {
  if (!repo?.trim() || !workdir?.trim()) {
    return undefined;
  }
  const parts = repo.trim().split('/').filter(Boolean);
  if (parts.length < 3) {
    return undefined;
  }
  return {
    organization: parts[0],
    repository: parts[parts.length - 1],
    project: parts.slice(1, -1).join('/'),
    workdir: workdir.trim(),
  };
}
